import { GeneriekBerichtOnderwerp, onderwerpen, usePostGeneriekBericht } from "@/entities/bericht";
import { DialogDescription, DialogTrigger, Form, SelectOption, TextAreaFormField } from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { Button } from "@/shared/components/ui/button.tsx";
import { Dialog, DialogBody, DialogContainer, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog.tsx";
import { SelectFormField } from "@/shared/components/ui/select-formfield.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { PropsWithChildren, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

const onderwerpOptions: SelectOption<GeneriekBerichtOnderwerp>[] = onderwerpen.map((onderwerp) => ({ value: onderwerp, label: onderwerp }));
const maxMessageLength = 450;

const nieuwBerichtSchema = z.object({
    onderwerp: z.enum(onderwerpen, { error: "Kies een onderwerp." }),
    bericht: z
        .string()
        .trim()
        .nonempty({
            message: "Voer een bericht in.",
        })
        .max(maxMessageLength, { message: "Maximaal 450 tekens toegestaan." }),
});

type NieuwBerichtDialogProps = {
    dossierId: string;
    prefilledOnderwerp?: GeneriekBerichtOnderwerp;
};

export function NieuwGeneriekBerichtDialog({ dossierId, prefilledOnderwerp, children }: PropsWithChildren<NieuwBerichtDialogProps>) {
    const [open, setOpen] = useState(false);
    const form = useForm<z.infer<typeof nieuwBerichtSchema>>({
        resolver: zodResolver(nieuwBerichtSchema),
        defaultValues: {
            onderwerp: prefilledOnderwerp ?? "Algemeen",
            bericht: "",
        },
    });
    const postGeneriekBericht = usePostGeneriekBericht();

    const handleSubmit: SubmitHandler<z.infer<typeof nieuwBerichtSchema>> = (data) => {
        postGeneriekBericht.mutate(
            { dossierId, onderwerp: data.onderwerp, bericht: data.bericht },
            {
                onSuccess: () => {
                    form.reset();
                    setOpen(false);
                },
            }
        );
    };

    const onOpenChange = (open: boolean) => {
        setOpen(open);
        if (!open) {
            form.reset();
            postGeneriekBericht.reset();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContainer className="min-w-[736px]">
                <DialogHeader>
                    <DialogTitle>Nieuw Wissel bericht</DialogTitle>
                    <DialogDescription className="sr-only">Schrijf een bericht naar Wissel</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(handleSubmit)(e)}>
                        <DialogBody className="grid gap-2">
                            <SelectFormField
                                form={form}
                                name="onderwerp"
                                options={onderwerpOptions}
                                label="Onderwerp"
                                placeholder="Selecteer onderwerp"
                                className="w-1/2"
                            />
                            <TextAreaFormField form={form} label="Bericht" name="bericht" maxLength={maxMessageLength} className="h-25" />
                            {postGeneriekBericht.error && (
                                <ErrorDisplay error={postGeneriekBericht.error} title="Fout bij het versturen van bericht" />
                            )}
                        </DialogBody>
                        <DialogFooter>
                            <Button type="button" variant="tertiary" onClick={() => onOpenChange(false)}>
                                Annuleren
                            </Button>
                            <Button type="submit" disabled={postGeneriekBericht.isPending}>
                                Versturen
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}
